import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { workOrderAssignmentRecordQuery } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderAssignmentQuery";
import { approveReceiptsWorkOrderAssignment } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderDriverMutations";
import { Suspense, useState } from "react";
import { Button } from "@/components/catalyst/button";
import { toast } from "sonner";
import { ExpensesSection } from "@/app/ProDrivers/WorkOrders/Components/Sheets/AssignmentSheetContent";
import { ProDriverReviewForm } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/ProDriverReviewDialog";

export function ApproveReceiptsDialog({
  workOrderAssignmentId,
}: {
  workOrderAssignmentId: string;
}) {
  return (
    <Suspense fallback={<div className="p-4 text-center">Loading...</div>}>
      <ApproveReceiptsContent workOrderAssignmentId={workOrderAssignmentId} />
    </Suspense>
  );
}

function ApproveReceiptsContent({
  workOrderAssignmentId,
}: {
  workOrderAssignmentId: string;
}) {
  const { data } = useSuspenseGqlQuery(workOrderAssignmentRecordQuery, {
    id: workOrderAssignmentId,
  });
  const { mutateAsync: approveReceipts } = useGqlMutation(
    approveReceiptsWorkOrderAssignment,
  );
  const { close, resolve } = useDialogState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [approved, setApproved] = useState(false);

  const assignment = data.workOrderAssignment;

  const handleApprove = async () => {
    setIsSubmitting(true);

    try {
      await approveReceipts({ id: workOrderAssignmentId });

      toast.success("Receipts approved");
      resolve?.({});
      setApproved(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (approved) {
    return (
      <BaseDialog
        title="Review Driver"
        subtitle="Leave a review for this driver"
        size="3xl"
      >
        <Suspense fallback={<div className="p-4 text-center">Loading...</div>}>
          <ProDriverReviewForm
            proDriverId={assignment.proDriver.id}
            workOrderAssignmentId={workOrderAssignmentId}
            onSubmitted={close}
          />
        </Suspense>
      </BaseDialog>
    );
  }

  return (
    <BaseDialog
      title="Approve Receipts"
      subtitle="Review the uploaded receipts and pending payout"
      size="3xl"
    >
      <div className="space-y-6">
        <ExpensesSection
          expenses={assignment.expenses}
          payouts={assignment.payouts}
          assignmentId={assignment.id}
          currency={assignment.workOrder.currency}
          taxRegistration={assignment.proDriver.tax_registration}
          assignmentStatus={assignment.status}
        />

        <div className="flex justify-end gap-2 border-t border-zinc-200 pt-4">
          <Button plain onClick={close} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="green"
            onClick={handleApprove}
            isLoading={isSubmitting}
            disabled={assignment.expenses.length === 0}
          >
            Approve Receipts
          </Button>
        </div>
      </div>
    </BaseDialog>
  );
}
